// Informe de servicio de una site (docs/PLAN-CIERRE-DIARIO.md, seccion 3 y
// docs/PLAN-UNIPAR-SOFT-LANDING.md): el informe de situacion inicial y el
// informe mensual son el MISMO artefacto con distinto periodo. Se arma
// agregando filas de metricas_diarias (una por dia y proveedor, mas la fila
// '*' del tenant); no recalcula nada from los hechos crudos.
//
// SIN DINERO POR DISENO: ninguna metrica de este informe se expresa en pesos
// ni en tarifas (regla 7 de CLAUDE.md, conflicto RU). La ociosidad se mide
// en asientos, los desvios en kilometros, las demoras en avisos.
//
// Politica de ausencia: `null` significa "sin dato en el periodo" (ningun
// dia con la metrica computable, o metrica que el cierre todavia no
// produce). Nunca se inventa un 0: un 0 es un hecho contado.
import { z } from "zod";
import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
import { SiteId, ProviderId } from "./common.js";
import { OperatingDate } from "./ride-requests.js";
// Solo ZodType: es lo unico que lee extendZodWithOpenApi (zod-to-openapi 9).
// Con el namespace entero como valor, Rolldown (Vite 8) lo materializa y las
// SPAs cargan todo zod con sus locales (unos 50 kB gzip mas); Rollup lo sacudia.
extendZodWithOpenApi({ ZodType: z.ZodType });
/** Tope del rango consultable: un trimestre largo (31+31+30). */
export const MAX_REPORT_DAYS = 92;
/** El informe en Markdown (text/markdown), listo para pegar en el documento de la site. */
export const ReportMarkdown = z.string().openapi("ReportMarkdown", {
    description: "Informe de servicio en Markdown (text/markdown; charset=utf-8).",
});
/**
 * Dias calendario inclusivos entre dos fechas AAAA-MM-DD, sin pasar por la
 * zona del navegador ni del servidor. Aritmetica de calendario, no de reloj:
 * las fechas operativas son dias de la site.
 */
export function inclusiveDays(from, to) {
    const start = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
    const end = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
    return Math.round((end - start) / 86_400_000) + 1;
}
export const ReportQuery = z
    .object({
    from: OperatingDate.openapi({
        description: "Primer dia operativo del periodo (inclusivo).",
        example: "2026-11-01",
    }),
    to: OperatingDate.openapi({
        description: "Ultimo dia operativo del periodo (inclusivo).",
        example: "2026-11-30",
    }),
})
    // El invariante (from <= to, tope de 92 dias) lo vuelve a verificar la
    // API en apps/api/src/report/calculation.ts (validRange): el refine es la
    // primera linea de defensa, no la unica.
    .refine((q) => q.from <= q.to, { message: "from no puede ser posterior a to" })
    .refine((q) => inclusiveDays(q.from, q.to) <= MAX_REPORT_DAYS, {
    message: `el periodo no puede superar ${String(MAX_REPORT_DAYS)} dias`,
})
    .openapi("ReportQuery", {
    description: "Periodo del informe: rango inclusivo de fechas operativas, maximo 92 dias. Un mes calendario para el informe mensual; las semanas de sombra para el informe de situacion inicial.",
});
const Integer = z.number().int().min(0).openapi({ format: "int32" });
const Decimal = z.number().openapi({ format: "double" });
const Percentage = z.number().min(0).max(100).openapi({ format: "double" });
export const ReportPeriod = z
    .object({
    from: OperatingDate,
    to: OperatingDate,
    days: Integer.openapi({
        description: "Dias calendario del periodo (inclusivo), tengan o no datos.",
    }),
})
    .openapi("ReportPeriod", { description: "Periodo cubierto por el informe." });
export const TripsByType = z
    .object({
    shuttle: Integer,
    cab: Integer,
    bus: Integer,
})
    .openapi("TripsByType", {
    description: "Viajes completados en el periodo, por tipo de vehiculo.",
});
export const DelaysByClassification = z
    .object({
    total: Integer.openapi({
        description: "Avisos de demora con clasificacion sellada por el tick en el periodo.",
    }),
    notifiedOnTime: Integer.openapi({
        description: "Demoras que el proveedor aviso dentro de la antelacion de la politica.",
    }),
    notifiedLate: Integer.openapi({
        description: "Demoras avisadas por el proveedor, pero fuera de la antelacion.",
    }),
    notNotified: Integer.openapi({
        description: "Demoras detectadas por la torre sin aviso del proveedor.",
    }),
})
    .openapi("DelaysByClassification", {
    description: "Cumplimiento de horarios: cuantas demoras hubo y como las comunico el proveedor. Es la metrica contractual de comunicacion.",
});
export const ExceptionByReason = z
    .object({
    reason: z.string().min(1).openapi({
        description: "Tipo de la excepcion operativa (delay_threshold, no_signal, etc.).",
        example: "delay_threshold",
    }),
    count: Integer,
})
    .openapi("ExceptionByReason", {
    description: "Conteo de excepciones operativas de un motivo en el periodo.",
});
export const JourneyOverrun = z
    .object({
    passengers: Integer.openapi({
        description: "Pasajeros cuyo trayecto real supero el tope contractual de 45 minutos, acumulado del periodo.",
    }),
    dailyMaxP95Min: Decimal.nullable().openapi({
        description: "Mayor p95 diario (minutos) de los trayectos excedidos: el peor dia del periodo. null si ningun dia registro exceso.",
    }),
})
    .openapi("JourneyOverrun", {
    description: "Cumplimiento del tope de trayecto de 45 minutos por pasajero.",
});
export const Kilometers = z
    .object({
    planned: Decimal.nullable().openapi({
        description: "Km del recorrido planificado, sumados sobre los dias con ambas mediciones. null sin dias medibles.",
    }),
    actual: Decimal.nullable().openapi({
        description: "Km del track GPS real, sumados sobre los mismos dias. null sin dias medibles.",
    }),
    deviationPct: Decimal.nullable().openapi({
        description: "(actual - planned) / planned * 100 sobre los totales del periodo. null si no hay km planificados.",
    }),
})
    .openapi("Kilometers", {
    description: "Kilometers planificados versus reales: exceso de recorrido, medido en km y no en dinero.",
});
export const MetricsSummary = z
    .object({
    tripsOperated: Integer.openapi({
        description: "Viajes completados en el periodo (suma de los cierres diarios).",
    }),
    tripsByType: TripsByType,
    averageOccupancyPct: Percentage.nullable().openapi({
        description: "Ocupacion promedio del periodo: promedio de la ocupacion diaria ponderado por viajes operados de cada dia. null si ningun dia tuvo ocupacion computable.",
    }),
    medianOccupancyPct: Percentage.nullable().openapi({
        description: "Mediana de la ocupacion diaria sobre los dias con dato: el dia tipico, inmune a un dia atipico. null sin dias con dato.",
    }),
    idleSeats: Integer.openapi({
        description: "Asientos vacios acumulados (capacidad menos abordados, viaje a viaje). La moneda de la ociosidad: asientos, no pesos.",
    }),
    noShows: Integer.openapi({
        description: "Pasajeros con reserva que no abordaron (estado no_show).",
    }),
    lateCancellations: Integer.nullable().openapi({
        description: "Reservas canceladas despues de la antelacion de la politica. null: el cierre diario todavia no las registra.",
    }),
    unusedBookings: Integer.openapi({
        description: "Reservas que no se convirtieron en viaje: noShows mas lateCancellations. Mientras lateCancellations sea null es una cota inferior (solo no-shows).",
    }),
    duplicateRequests: Integer.openapi({
        description: "Mismo legajo con mas de una reserva para el mismo turno y direction. Solo se computa en la fila del tenant; por proveedor es 0.",
    }),
    delays: DelaysByClassification,
    exceptionsTotal: Integer.openapi({
        description: "Excepciones operativas abiertas en el periodo, todos los motivos.",
    }),
    exceptionsByReason: z.array(ExceptionByReason).openapi({
        description: "Excepciones por motivo, de mayor a menor cantidad.",
    }),
    meanResolutionTimeMin: Decimal.nullable().openapi({
        description: "Minutos promedio para resolver una excepcion, ponderado por excepciones de cada dia. null sin excepciones resueltas.",
    }),
    handoverOnTimePct: Percentage.nullable().openapi({
        description: "Porcentaje de pasajeros de ingreso que llegaron antes del relevo con el margen de la politica, ponderado por viajes operados de cada dia. null sin viajes de ingreso medibles.",
    }),
    kilometers: Kilometers,
    journeyOver45: JourneyOverrun,
})
    .openapi("MetricsSummary", {
    description: "Agregado de metricas de servicio sobre un periodo. Sin dinero por diseno. null = sin dato, nunca un 0 inventado.",
});
export const ProviderReportSummary = MetricsSummary.extend({
    providerId: ProviderId,
    daysWithData: Integer.openapi({
        description: "Dias del periodo con fila de cierre para este proveedor.",
    }),
}).openapi("ProviderReportSummary", {
    description: "Las mismas metricas, acotadas a un proveedor de transporte.",
});
export const DailyPoint = z
    .object({
    date: OperatingDate,
    tripsOperated: Integer,
    averageOccupancyPct: Percentage.nullable(),
    idleSeats: Integer,
    noShows: Integer,
    delaysTotal: Integer,
    delaysNotNotified: Integer,
    handoverOnTimePct: Percentage.nullable(),
    exceptionsTotal: Integer,
})
    .openapi("DailyPoint", {
    description: "Un dia de la serie del informe (fila '*' del cierre): lo justo para graficar la evolucion del periodo.",
});
export const ReportResponse = z
    .object({
    site: SiteId,
    period: ReportPeriod,
    daysWithData: Integer.openapi({
        description: "Dias del periodo con cierre diario del tenant. Menor que period.days significa dias sin operacion o sin cierre corrido.",
    }),
    calculationVersion: Integer.nullable().openapi({
        description: "Version de las formulas del cierre con la que se computaron las filas (la mayor si hay mezcla). null sin filas.",
    }),
    mixedVersions: z.boolean().openapi({
        description: "true si el periodo mezcla filas computadas con distintas versiones de formula: el informe lo aclara.",
    }),
    global: MetricsSummary.openapi({
        description: "Metricas del tenant completo (fila '*' de cada dia).",
    }),
    byProvider: z.array(ProviderReportSummary).openapi({
        description: "Una entrada por proveedor con filas en el periodo, ordenadas por id.",
    }),
    dailySeries: z.array(DailyPoint).max(MAX_REPORT_DAYS).openapi({
        description: "Un punto por dia con cierre, del mas viejo al mas reciente.",
    }),
})
    .openapi("ReportResponse", {
    description: "Informe de servicio de una planta sobre un periodo: el informe de situacion inicial y el mensual son este mismo shape. Sin dinero por diseno.",
});
//# sourceMappingURL=report.js.map